import {
  Blocks,
  Events,
  Extensions,
  defineBlocksWithJsonArray,
  type Block,
} from 'blockly/core';

import { BEGINNER_DISPLAY_BLOCK_STYLE } from './display-helpers.js';
import {
  OLED_FILL_COLOR_OPTIONS,
  OLED_TEXT_SIZE_OPTIONS,
  oledSizeField,
  oledXField,
  oledYField,
} from './oled-fields.js';
import {
  DISPLAY_POSITION_LINE_COLUMN,
  createOledPositionModeDropdown,
  updateOledPositionDetails,
} from './oled-position.js';
import { registerBeginnerOledConfigureBlock } from './oled-configure-block.js';

const OLED_POSITION_UI_EXTENSION = 'beginner_oled_position_ui';

function registerOledPositionUiExtension(): void {
  if (Extensions.isRegistered(OLED_POSITION_UI_EXTENSION)) {
    return;
  }

  Extensions.register(OLED_POSITION_UI_EXTENSION, function (this: Block) {
    this.setOnChange((event) => {
      if (event.type === Events.FINISHED_LOADING) {
        queueMicrotask(() => updateOledPositionDetails(this, undefined, true));
        return;
      }

      if (
        event.type === Events.BLOCK_CHANGE &&
        'blockId' in event &&
        event.blockId === this.id &&
        'name' in event &&
        event.name === 'POSITION_MODE'
      ) {
        const newValue =
          'newValue' in event && event.newValue != null ? String(event.newValue) : undefined;
        queueMicrotask(() => {
          if (!this.isDisposed()) {
            updateOledPositionDetails(this, newValue, true);
          }
        });
      }
    });
  });
}

function applyOledStatementDefaults(block: Block, tooltip: string): void {
  block.setPreviousStatement(true, null);
  block.setNextStatement(true, null);
  block.setStyle(BEGINNER_DISPLAY_BLOCK_STYLE);
  block.setTooltip(tooltip);
  block.setHelpUrl('');
}

function registerOledPositionBlocks(): void {
  registerOledPositionUiExtension();

  if (!Blocks['beginner_oled_display_text']) {
    Blocks['beginner_oled_display_text'] = {
      init(this: Block) {
        this.appendValueInput('TEXT')
          .setCheck('String')
          .appendField('OLED display text');
        this.appendDummyInput('MODE_ROW')
          .appendField('at')
          .appendField(createOledPositionModeDropdown(this), 'POSITION_MODE');
        applyOledStatementDefaults(this, 'Shows text on the OLED at a line/column or x/y position.');
        Extensions.apply(OLED_POSITION_UI_EXTENSION, this, false);
        this.setFieldValue(DISPLAY_POSITION_LINE_COLUMN, 'POSITION_MODE');
        updateOledPositionDetails(this, DISPLAY_POSITION_LINE_COLUMN, true);
      },
    };
  }

  if (!Blocks['beginner_oled_set_cursor']) {
    Blocks['beginner_oled_set_cursor'] = {
      init(this: Block) {
        this.appendDummyInput('HEADER')
          .appendField('OLED set cursor')
          .appendField(createOledPositionModeDropdown(this), 'POSITION_MODE');
        applyOledStatementDefaults(
          this,
          'Moves the OLED cursor using line/column or x/y coordinates.',
        );
        Extensions.apply(OLED_POSITION_UI_EXTENSION, this, false);
        this.setFieldValue(DISPLAY_POSITION_LINE_COLUMN, 'POSITION_MODE');
        updateOledPositionDetails(this, DISPLAY_POSITION_LINE_COLUMN, true);
      },
    };
  }
}

/**
 * Static OLED blocks (configure, clear, drawing, etc.).
 * Position-mode blocks are registered separately so fields can swap cleanly.
 */
const oledStaticBlockDefinitions = [
  {
    type: 'beginner_oled_clear',
    message0: 'OLED clear display',
    previousStatement: null,
    nextStatement: null,
    style: BEGINNER_DISPLAY_BLOCK_STYLE,
    tooltip: 'Clears all content from the OLED display.',
    helpUrl: '',
  },
  {
    type: 'beginner_oled_set_text_size',
    message0: 'OLED set text size %1',
    args0: [
      {
        type: 'field_dropdown',
        name: 'SIZE',
        options: OLED_TEXT_SIZE_OPTIONS,
      },
    ],
    previousStatement: null,
    nextStatement: null,
    style: BEGINNER_DISPLAY_BLOCK_STYLE,
    tooltip: 'Sets how large OLED text appears (1–5).',
    helpUrl: '',
  },
  {
    type: 'beginner_oled_draw_pixel',
    message0: 'OLED draw pixel x %1 y %2',
    args0: [oledXField('X'), oledYField('Y')],
    previousStatement: null,
    nextStatement: null,
    style: BEGINNER_DISPLAY_BLOCK_STYLE,
    tooltip: 'Draws one pixel on the OLED.',
    helpUrl: '',
  },
  {
    type: 'beginner_oled_draw_line',
    message0: 'OLED draw line from x %1 y %2 to x %3 y %4',
    args0: [oledXField('X1'), oledYField('Y1'), oledXField('X2', 20), oledYField('Y2', 20)],
    previousStatement: null,
    nextStatement: null,
    style: BEGINNER_DISPLAY_BLOCK_STYLE,
    tooltip: 'Draws a line between two points on the OLED.',
    helpUrl: '',
  },
  {
    type: 'beginner_oled_draw_rectangle',
    message0: 'OLED draw rectangle x %1 y %2 width %3 height %4',
    args0: [
      oledXField('X'),
      oledYField('Y'),
      oledSizeField('WIDTH', 20),
      oledSizeField('HEIGHT', 10, 64),
    ],
    previousStatement: null,
    nextStatement: null,
    style: BEGINNER_DISPLAY_BLOCK_STYLE,
    tooltip: 'Draws a rectangle outline on the OLED.',
    helpUrl: '',
  },
  {
    type: 'beginner_oled_fill_rectangle',
    message0: 'OLED fill rectangle x %1 y %2 width %3 height %4',
    args0: [
      oledXField('X'),
      oledYField('Y'),
      oledSizeField('WIDTH', 20),
      oledSizeField('HEIGHT', 10, 64),
    ],
    previousStatement: null,
    nextStatement: null,
    style: BEGINNER_DISPLAY_BLOCK_STYLE,
    tooltip: 'Fills a rectangle on the OLED.',
    helpUrl: '',
  },
  {
    type: 'beginner_oled_draw_circle',
    message0: 'OLED draw circle center x %1 center y %2 radius %3',
    args0: [oledXField('CX', 32), oledYField('CY', 16), oledSizeField('RADIUS', 8, 64)],
    previousStatement: null,
    nextStatement: null,
    style: BEGINNER_DISPLAY_BLOCK_STYLE,
    tooltip: 'Draws a circle outline on the OLED.',
    helpUrl: '',
  },
  {
    type: 'beginner_oled_fill_circle',
    message0: 'OLED fill circle center x %1 center y %2 radius %3',
    args0: [oledXField('CX', 32), oledYField('CY', 16), oledSizeField('RADIUS', 8, 64)],
    previousStatement: null,
    nextStatement: null,
    style: BEGINNER_DISPLAY_BLOCK_STYLE,
    tooltip: 'Fills a circle on the OLED.',
    helpUrl: '',
  },
  {
    type: 'beginner_oled_draw_triangle',
    message0: 'OLED draw triangle x1 %1 y1 %2 x2 %3 y2 %4 x3 %5 y3 %6',
    args0: [
      oledXField('X1', 20),
      oledYField('Y1', 40),
      oledXField('X2', 60),
      oledYField('Y2', 40),
      oledXField('X3', 40),
      oledYField('Y3', 10),
    ],
    previousStatement: null,
    nextStatement: null,
    style: BEGINNER_DISPLAY_BLOCK_STYLE,
    tooltip: 'Draws a triangle outline on the OLED.',
    helpUrl: '',
  },
  {
    type: 'beginner_oled_fill_screen',
    message0: 'OLED fill screen %1',
    args0: [
      {
        type: 'field_dropdown',
        name: 'COLOR',
        options: OLED_FILL_COLOR_OPTIONS,
      },
    ],
    previousStatement: null,
    nextStatement: null,
    style: BEGINNER_DISPLAY_BLOCK_STYLE,
    tooltip: 'Fills the entire OLED with white or black.',
    helpUrl: '',
  },
  {
    type: 'beginner_oled_refresh',
    message0: 'OLED refresh display',
    previousStatement: null,
    nextStatement: null,
    style: BEGINNER_DISPLAY_BLOCK_STYLE,
    tooltip: 'Updates the OLED so drawing and text changes become visible.',
    helpUrl: '',
  },
];

export function registerBeginnerOledBlocks(): void {
  registerBeginnerOledConfigureBlock();
  registerOledPositionBlocks();
  defineBlocksWithJsonArray(oledStaticBlockDefinitions);
}
